import {ICurriculumsService} from './ICurriculumsService';
import {CurriculumsRepository} from './CurriculumsRepository';
import {Curriculum} from '../../graphql/API';
import {LevelsRepository} from '../Level/LevelsRepository';
import {PlansRepository} from '../Plans/PlansRepository';
import {fetchUserAttributes} from 'aws-amplify/auth';
import {NewCreateCurriculumInput} from '@PikaDoApp/npm-pikado-logic/dist/modules/curriculum/graphql/curriculum.interface';
import {CurriculumTypeEnum} from '@PikaDoApp/npm-pikado-logic';

/**
 * CORRECTED TYPE DEFINITION for UpdateCurriculumInput
 *
 * Note: The package type definition (@PikaDoApp/npm-pikado-logic) incorrectly defines
 * placementTest as a single string, but the API actually expects an array of strings.
 * This local interface provides the correct type signature.
 */
interface CurriculumUpdateInput {
  id: string;
  name?: string;
  type?: CurriculumTypeEnum;
  program?: string;
  createdBy?: string;
  level?: string[];
  placementTest?: string[]; // ✅ CORRECT: Array of placement test IDs
}

export class CurriculumsService implements ICurriculumsService {
  curriculumsRepository: CurriculumsRepository;
  levelsRepository: LevelsRepository;
  planRepository: PlansRepository;

  constructor() {
    this.curriculumsRepository = new CurriculumsRepository();
    this.levelsRepository = new LevelsRepository();
    this.planRepository = new PlansRepository();
  }

  async getCurriculums(): Promise<any> {
    let curriculums = await this.curriculumsRepository.getCurriculums();
    return curriculums;
  }

  async getCurriculumById(id: string): Promise<any> {
    let curriculum = await this.curriculumsRepository.getCurriculumById(id);
    return curriculum;
  }

  /**
   * Creates a new curriculum using the @PikaDoApp/npm-pikado-logic package
   *
   * ⚠️ IMPORTANT: Two-Step Process for Placement Tests
   * The CREATE API does NOT support the placementTest field in NewCreateCurriculumInput.
   * Therefore, if placement tests are provided, this method:
   * 1. First creates the curriculum WITHOUT placement tests
   * 2. Then immediately updates the curriculum to ADD placement tests
   *
   * @param curriculum - The curriculum object containing the name
   * @param selectedProgramId - The program ID to associate with the curriculum
   * @param selectedLanguageId - The language ID (currently not used in new implementation)
   * @param selectedLevelIds - Array of level IDs to associate with the curriculum
   * @param selectedPlacementTestIds - Optional array of placement test IDs (added via UPDATE after creation)
   * @returns The created curriculum object
   *
   * @example
   * ```typescript
   * const curriculum = { name: "Test Curriculum" };
   * const result = await createNewCurriculum(
   *   curriculum,
   *   "68f8a6a815abe9bbe3c5f70b", // programId
   *   "language-id",
   *   ["68d3776fafab496640df1fa4"], // levelIds
   *   ["68d3779517d58c334e516522", "68d3779c17d58c334e516546"] // placementTestIds
   * );
   * ```
   */
  async createNewCurriculum(
    curriculum: Curriculum,
    selectedProgramId: string,
    selectedLanguageId: string,
    selectedLevelIds: string[],
    curriculumType: CurriculumTypeEnum,
    selectedPlacementTestIds?: string[],
  ): Promise<any> {
    try {
      // Get current user for createdBy field
      const currentUser = await fetchUserAttributes();
      const userId = currentUser.sub || 'unknown';

      // Prepare curriculum data for new logic package
      const curriculumToCreate: NewCreateCurriculumInput = {
        name: curriculum.name,
        type: curriculumType,
        level: selectedLevelIds,
        program: selectedProgramId,
        createdBy: userId,
        placementTest: selectedPlacementTestIds,
      };

      // Create curriculum using new logic package
      const createdCurriculum =
        await this.curriculumsRepository.createNewCurriculum(
          curriculumToCreate,
        );

      // If placement tests are selected, update the curriculum to add them
      // (The create API doesn't support placement tests, so we update after creation)
      if (selectedPlacementTestIds && selectedPlacementTestIds.length > 0) {
        // Create a minimal curriculum object with only the name
        const minimalCurriculum: any = {
          name: curriculum.name,
        };

        const updateResult = await this.updateNewCurriculum(
          createdCurriculum.id, // Use 'id' not '_id' (repository returns 'id')
          minimalCurriculum,
          undefined, // Don't update program
          undefined, // Don't update levels
          selectedPlacementTestIds,
        );
        return updateResult;
      } else {
        console.log(
          '🔍 [CURRICULUM-CREATE] No placement tests provided, skipping UPDATE step',
        );
      }
      return createdCurriculum;
    } catch (error) {
      console.error(
        '🔍 [CURRICULUM-CREATE] ❌ ERROR during curriculum creation:',
        error,
      );
      throw error;
    }
  }

  /**
   * Updates an existing curriculum using the @PikaDoApp/npm-pikado-logic package
   *
   * ✅ The UPDATE API DOES support the placementTest field (unlike CREATE)
   *
   * @param id - The curriculum ID to update
   * @param curriculum - The curriculum object containing fields to update
   * @param selectedProgramId - Optional program ID to update
   * @param selectedLevelIds - Optional array of level IDs to update
   * @param selectedPlacementTestIds - Optional array of placement test IDs to update
   *   - If provided with values: Sets the placement tests to this array
   *   - If provided as empty array []: Clears all placement tests
   *   - If undefined: Does not modify placement tests
   * @returns The updated curriculum object
   *
   * @example
   * ```typescript
   * // Add placement tests to existing curriculum
   * await updateNewCurriculum(
   *   "curriculum-id",
   *   { name: "Updated Name" },
   *   undefined, // Don't update program
   *   undefined, // Don't update levels
   *   ["68d3779517d58c334e516522", "68d3779c17d58c334e516546"] // Set placement tests
   * );
   *
   * // Clear placement tests
   * await updateNewCurriculum(
   *   "curriculum-id",
   *   { name: "Updated Name" },
   *   undefined,
   *   undefined,
   *   [] // Clear placement tests
   * );
   * ```
   */
  async updateNewCurriculum(
    id: string,
    curriculum: Curriculum,
    selectedProgramId?: string,
    selectedLevelIds?: string[],
    selectedPlacementTestIds?: string[],
    curriculumType?: CurriculumTypeEnum,
  ): Promise<any> {
    try {
      // Prepare update input with proper typing
      const updateInput: CurriculumUpdateInput = {
        id: id,
      };

      // Only include fields that are provided
      if (curriculum.name) {
        updateInput.name = curriculum.name;
      }
      if (curriculumType) {
        updateInput.type = curriculumType;
      }
      if (selectedProgramId) {
        updateInput.program = selectedProgramId;
      }
      if (selectedLevelIds && selectedLevelIds.length > 0) {
        updateInput.level = selectedLevelIds;
      }
      if (selectedPlacementTestIds !== undefined) {
        // Handle placement test IDs array
        // Note: Using our local CurriculumUpdateInput type which correctly defines
        // placementTest as string[] (the package incorrectly defines it as string)
        if (selectedPlacementTestIds.length > 0) {
          updateInput.placementTest = selectedPlacementTestIds;
        } else {
          // Empty array means clearing placement tests
          updateInput.placementTest = [];
        }
      }

      // Update curriculum using new logic package
      const updatedCurriculum =
        await this.curriculumsRepository.updateNewCurriculum(updateInput);

      return updatedCurriculum;
    } catch (error) {
      console.error(
        '🔍 [CURRICULUM-NEW-PACKAGE] updateNewCurriculum service Error:',
        error,
      );
      throw error;
    }
  }

  // NEW IMPLEMENTATION - Using @PikaDoApp/npm-pikado-logic package
  async softDeleteNewCurriculum(id: string): Promise<boolean> {
    try {
      const result =
        await this.curriculumsRepository.softDeleteNewCurriculum(id);
      return result;
    } catch (error) {
      console.error(
        '🔍 [CURRICULUM-NEW-PACKAGE] softDeleteNewCurriculum service Error:',
        error,
      );
      throw error;
    }
  }
}
